import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Plane, Shield, StopCircle, MapPin, Clock } from "lucide-react";
import { MapContainer, TileLayer, Marker, Circle, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map(p => [p[0], p[1]]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
  }, [points, map]);
  return null;
}

const TRACK_INTERVAL_MS = 5 * 60 * 1000;

function formatDuration(fromIso) {
  const diff = Date.now() - new Date(fromIso).getTime();
  const mins = Math.floor(diff / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Widget "Trasferta in corso" per portale dipendente.
 * Si nasconde automaticamente se non c'è una sessione attiva.
 * Avvia il tracking GPS ogni 5 minuti finché è montato e la sessione è attiva.
 */
export default function TrasfertaWidget() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const trackingRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/trasferta/my-active");
      setSession(r.data || null);
    } catch { setSession(null); }
    finally { setLoading(false); }
  };

  const pushPoint = async () => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        try {
          await api.post("/trasferta/track", {
            latitude: p.coords.latitude,
            longitude: p.coords.longitude,
            accuracy_m: p.coords.accuracy,
          });
          load();
        } catch { /* silent */ }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const startTracking = () => {
    if (trackingRef.current) return;
    trackingRef.current = setInterval(pushPoint, TRACK_INTERVAL_MS);
  };
  const stopTracking = () => {
    if (trackingRef.current) { clearInterval(trackingRef.current); trackingRef.current = null; }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (session?.active) startTracking();
    return () => stopTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  const handleStop = async () => {
    if (!confirm("Confermi l'interruzione del tracking GPS trasferta? I dati già raccolti restano conservati fino a fine mese e poi vengono cancellati automaticamente (GDPR).")) return;
    try {
      await api.post("/trasferta/stop");
      toast.success("Tracking trasferta interrotto");
      stopTracking();
      setSession(null);
    } catch { toast.error("Errore stop"); }
  };

  if (loading || !session) return null;

  const trail = (session.points || []).map(p => [p.lat, p.lng]);
  const lastPoint = trail.length ? trail[trail.length - 1] : null;

  return (
    <div className="card-smart overflow-hidden border-[#3B82F6]/40 bg-gradient-to-br from-[#1E2347] to-[#0A0E27]" data-testid="portal-trasferta-widget">
      <div className="p-5 border-b border-[#1F2540] flex items-center gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#3B82F6] flex items-center justify-center flex-shrink-0 animate-pulse">
          <Plane className="w-6 h-6 text-white"/>
        </div>
        <div className="flex-1 min-w-40">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#3B82F6]">Trasferta in corso</span>
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"/>
          </div>
          <div className="font-display text-xl">{session.start_qr_label}</div>
          <div className="text-xs text-[#757575] flex items-center gap-3 mt-1 flex-wrap">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3"/> durata {formatDuration(session.started_at)}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/> {session.points?.length || 0} punti GPS</span>
            <span className="flex items-center gap-1"><Shield className="w-3 h-3"/> GDPR · conservazione fine mese</span>
          </div>
        </div>
        <Button onClick={handleStop} variant="outline" data-testid="portal-stop-trasferta" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
          <StopCircle className="w-4 h-4 mr-1"/> Interrompi
        </Button>
      </div>

      {trail.length > 0 && (
        <div style={{ height: 260 }}>
          <MapContainer center={trail[0]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {lastPoint && <Marker position={lastPoint}/>}
            {trail.length > 0 && (
              <Circle center={trail[0]} radius={50} pathOptions={{ color: "#059669", fillColor: "#059669", fillOpacity: 0.3, weight: 2 }}/>
            )}
            {trail.length > 1 && (
              <Polyline positions={trail} pathOptions={{ color: "#3B82F6", weight: 4, dashArray: "8 4" }}/>
            )}
            <FitBounds points={trail}/>
          </MapContainer>
        </div>
      )}

      <div className="px-5 py-3 text-[10px] text-[#757575] bg-white/60 border-t border-[#1F2540]">
        I dati GPS vengono raccolti ogni 5 minuti, utilizzati solo per la verifica trasferta e cancellati automaticamente a fine mese (Reg. UE 2016/679 · art. 6.1.b · minimizzazione).
      </div>
    </div>
  );
}
